// 全局错误边界：页面渲染抛错（组件 bug / 数据形态异常）时展示整页中文错误页 + 刷新按钮，
// 替代默认白屏。渲染崩溃时页面上下文不可信，故不走 toast。
//
// 注入式下沉（FE1-019）：下沉前 admin 与 agent/merchant 仅 2 行品牌口径差异
// （联系对象文案 + 主按钮配色），经 brand props 参数化：
//   - 'platform'（缺省，多数端/平台后台口径）：「平台管理员」+ brand-600 主题按钮；
//   - 'business'（B 端口径，agent/merchant 注入）：「平台客服」+ blue-600 按钮。
// customer 端为 i18n 化实现（3 处文案 + 配色），差异超出品牌口径，不并入本组件。
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** 品牌口径：'platform'=平台后台（联系平台管理员 / brand 色按钮，缺省）；'business'=B 端（联系平台客服 / blue 色按钮） */
  brand?: 'platform' | 'business';
}

interface State {
  error: Error | null;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // 控制台保留原始堆栈供排查（渲染崩溃时无可靠上报通道）
    console.error('[ErrorBoundary] 页面渲染异常', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      const isBusiness = this.props.brand === 'business';
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
          <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl shadow-sm p-8 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-500" />
            </div>
            <h1 className="mt-4 text-base font-semibold text-slate-900">页面发生错误</h1>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">
              {isBusiness
                ? '页面渲染出现异常，刷新通常可恢复；若反复出现请联系平台客服并附下方错误信息。'
                : '页面渲染出现异常，刷新通常可恢复；若反复出现请联系平台管理员并附下方错误信息。'}
            </p>
            <p className="mt-3 text-xs text-slate-400 break-all bg-slate-50 rounded-md p-2 text-left font-mono">
              {this.state.error.message || String(this.state.error)}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={
                isBusiness
                  ? 'mt-5 w-full inline-flex justify-center items-center px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors'
                  : 'mt-5 w-full inline-flex justify-center items-center px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors'
              }
            >
              刷新页面
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
